// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-RBAC-2 — THE ONE client-side definition of how a permission set derives the legacy role bucket.
// MUST mirror functions/Auth/RoleBuckets.cs list for list and branch for branch (L-068). The C# test
// RoleBucketsParityTests parses THIS file and fails the build when the lists drift. Keep the arrays
// as plain string literals on one line each so the parser stays trivial.

export type RoleBucket = 'admin' | 'manufacturer' | 'facility' | 'support' | 'pharmacy_staff' | 'provider' | 'provider_nurse' | 'patient'

export const PORTAL_PHARMACY = 'portal.pharmacy'
export const ADMIN_PREFIXES = ['admin.']
export const MANUFACTURER_PREFIXES = ['manufacturer.']
export const SUPPORT_EXACT = ['support.tickets.work', 'support.queue.read']
export const STAFF_PREFIXES = ['rx.verify', 'rx.fill', 'rx.dispense']
export const PROVIDER_PREFIXES = ['rx.write']
export const PHARMACY_OPS_PREFIXES = ['shipping.', 'refills.', 'payer.', 'rx.fill', 'rx.partial_fill', 'rx.queue']
export const NURSE_EXACT = ['rx.comment', 'rx.read']
export const PATIENT_PREFIXES = ['patient.']
// D-SOC-1 — external facility staff (SNF / nursing home). Only the Facility Staff role (mig 399)
// carries a facility.* permission, so no existing account's derived role changes.
export const FACILITY_PREFIXES = ['facility.']

/** Same order as RoleBuckets.Derive: 'admin.' wins over everything. null = no bucket (the server says "authenticated"). */
export function deriveRoleBucket(perms: string[] | undefined | null): RoleBucket | null {
  if (!perms || perms.length === 0) return null
  const has = (prefix: string) => perms.some(p => p.startsWith(prefix))
  const exact = (key: string) => perms.includes(key)
  const anyPrefix = (ps: string[]) => ps.some(has)

  if (anyPrefix(ADMIN_PREFIXES)) return 'admin'
  if (anyPrefix(MANUFACTURER_PREFIXES)) return 'manufacturer'
  // BEFORE pharmacy_staff / provider / nurse — the Facility Staff role carries rx.read, so a later
  // branch could promote an external nursing-home login into a pharmacy-portal role.
  if (anyPrefix(FACILITY_PREFIXES)) return 'facility'
  if (SUPPORT_EXACT.some(exact)) return 'support'
  if (exact(PORTAL_PHARMACY) || anyPrefix(STAFF_PREFIXES)) return 'pharmacy_staff'
  if (anyPrefix(PROVIDER_PREFIXES)) return 'provider'

  const hasOps = anyPrefix(PHARMACY_OPS_PREFIXES)
  if (NURSE_EXACT.every(exact) && !hasOps) return 'provider_nurse'
  if (hasOps) return 'pharmacy_staff'

  if (anyPrefix(PATIENT_PREFIXES)) return 'patient'
  return null
}
